/**
 * StarterChooser — modal that opens when operator clicks "+ New X".
 *
 * Phase 8 Prompt 3 — per audit deliverable 04D Pattern P2 (clone-to-create,
 * never blank canvas) + Section E reference implementation.
 *
 * Renders a 2-column grid of starter cards from the entity-type's JSON file,
 * plus a "Blank Canvas" tile as a smaller secondary CTA. Operator picks one;
 * `onSelect(starter)` fires; modal closes.
 *
 * Usage:
 *   const [chooserOpen, setChooserOpen] = useState(false);
 *   <Button onClick={() => setChooserOpen(true)}>+ New Funnel</Button>
 *   <StarterChooser
 *     entityType="funnel"
 *     open={chooserOpen}
 *     onClose={() => setChooserOpen(false)}
 *     onSelect={(starter) => handleStartFromStarter(starter)}
 *   />
 */

import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Import all starter JSONs (bundled at build time).
import funnelsStarters from "../../data/starters/funnels.json";
import campaignsStarters from "../../data/starters/campaigns.json";
import audiencesStarters from "../../data/starters/audiences.json";
import templatesStarters from "../../data/starters/templates.json";
import triggersStarters from "../../data/starters/triggers.json";

export type EntityType =
  | "funnel"
  | "campaign"
  | "audience"
  | "template"
  | "trigger";

// Generic starter shape; specific entity types may have extra fields beyond these.
export interface StarterEntry {
  id: string;
  name: string;
  description?: string;
  useCase?: string;
  icon?: string;
  category?: string | null;
  // Extras are passed through verbatim to onSelect
  [key: string]: unknown;
}

interface StarterRegistry {
  starters: StarterEntry[];
}

const REGISTRIES: Record<EntityType, StarterRegistry> = {
  funnel: funnelsStarters as unknown as StarterRegistry,
  campaign: campaignsStarters as unknown as StarterRegistry,
  audience: audiencesStarters as unknown as StarterRegistry,
  template: templatesStarters as unknown as StarterRegistry,
  trigger: triggersStarters as unknown as StarterRegistry,
};

const ENTITY_LABELS: Record<EntityType, { singular: string; modalTitle: string }> = {
  funnel: { singular: "funnel", modalTitle: "Create a new funnel" },
  campaign: { singular: "campaign", modalTitle: "Create a new campaign" },
  audience: { singular: "audience", modalTitle: "Create a new audience" },
  template: { singular: "template", modalTitle: "Create a new template" },
  trigger: { singular: "trigger", modalTitle: "Create a new trigger" },
};

export interface StarterChooserProps {
  entityType: EntityType;
  open: boolean;
  onClose: () => void;
  /** Called with the selected starter (or "blank" sentinel for blank canvas). */
  onSelect: (starter: StarterEntry) => void;
}

export const StarterChooser: React.FC<StarterChooserProps> = ({
  entityType,
  open,
  onClose,
  onSelect,
}) => {
  const registry = REGISTRIES[entityType];
  const labels = ENTITY_LABELS[entityType];

  // Separate "blank" starter (always rendered as secondary tile at bottom).
  const { branded, blank } = useMemo(() => {
    const blankStarter = registry.starters.find(
      (s) => s.id.startsWith("blank") || s.name.toLowerCase().includes("blank"),
    );
    const brandedStarters = registry.starters.filter((s) => s !== blankStarter);
    return { branded: brandedStarters, blank: blankStarter ?? null };
  }, [registry]);

  const handlePick = (starter: StarterEntry) => {
    onSelect(starter);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="starter-chooser-title"
    >
      <DialogTitle
        id="starter-chooser-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: '"Playfair Display", serif',
          fontWeight: 700,
          color: "#1A5F7A",
        }}
      >
        {labels.modalTitle}
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 2, lineHeight: 1.6 }}
        >
          Start from a brand-recommended template (recommended), or build from a
          blank canvas. You can edit everything before publishing.
        </Typography>

        <Typography
          variant="overline"
          sx={{
            color: "text.secondary",
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            letterSpacing: "1px",
          }}
        >
          Start from a template
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
            mt: 1,
            mb: 2,
          }}
        >
          {branded.map((starter) => (
            <Card
              key={starter.id}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                transition: "border-color 200ms, box-shadow 200ms",
                "&:hover": {
                  borderColor: "#1A5F7A",
                  boxShadow: "0 2px 8px rgba(26,95,122,0.12)",
                },
              }}
            >
              <CardActionArea
                onClick={() => handlePick(starter)}
                sx={{ p: 0, height: "100%" }}
              >
                <CardContent sx={{ display: "flex", gap: 1.5 }}>
                  {starter.icon && (
                    <Box
                      sx={{
                        fontSize: "1.6rem",
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      {starter.icon}
                    </Box>
                  )}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 600,
                        color: "#1A5F7A",
                        lineHeight: 1.2,
                      }}
                    >
                      {starter.name}
                    </Typography>
                    {starter.useCase && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary",
                          fontStyle: "italic",
                          mb: 0.5,
                        }}
                      >
                        {starter.useCase}
                      </Typography>
                    )}
                    {starter.description && (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary", lineHeight: 1.45 }}
                      >
                        {starter.description}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {blank && (
          <>
            <Divider sx={{ my: 2 }} />
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderStyle: "dashed",
                bgcolor: "transparent",
              }}
            >
              <CardActionArea
                onClick={() => handlePick(blank)}
                sx={{ p: 0 }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 2,
                    gap: 1,
                  }}
                >
                  <Box sx={{ fontSize: "1.2rem" }} aria-hidden>
                    {blank.icon ?? "📝"}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    {blank.name} — start from scratch
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StarterChooser;
